// ============================================
// useAiChatServer — 씬 클라이언트 훅 (C′ 통합설계 §8, ai_manager_refact)
// ============================================
// 에이전트 루프는 node-agent 서버가 실행 — 브라우저는 질문 접수(POST api3/ai-agent/run/accept) 후
// 결과를 짧게 반복 조회(GET api3/ai-agent/run/{turnId})한다. 연결을 오래 붙잡지 않아 재배포에도 답을 잃지 않는다.
// useAiChat(브라우저 루프)과 동일 반환 인터페이스 유지 → AiChattingView 등 기존 컴포넌트 무수정 재사용.
// OpenAI 키·에이전트 세션·버퍼·텔레메트리는 전부 서버 소유. 세션 목록/복원/평가/SMS 발송은 기존 Spring API 직접 호출 유지.

import { useState, useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import FetchModule from '../../Model/Share/Network/FetchModule';
import { RESPONSE_FAILED_RETRY, CANCELLED_RESPONSE, TOOL_CALLING_LOG } from '../../AiManager/Model/aiConfig';
import { createSmsHandlers, restoreSmsFromSave } from '../../AiManager/Transaction/smsHandlers';
import { detectResponseLanguage } from '../../Agent/Tools/responseLanguage';
import { detectSpeechRegister } from '../../AiManager/Utils/aiUtils';

// 폴링 간격(초) — 앞 3회는 1·2·3, 그 뒤로는 고정 간격 //
const POLL_INTERVAL_SECONDS = [1, 2, 3];
const POLL_FIXED_INTERVAL_SECONDS = 3;

// 폴링 총 대기 상한 — 턴 하드 상한 330 + 텔레메트리 저장 최악 95 + 여유 10.
// 백엔드 API3AiAgentService.RUNNING_STALE_SECONDS와 같은 값이라 서버도 이 시점에 failed로 판정한다.
// 서버가 AGENT_TURN_BUDGET_MS / TURN_TIMEOUT_MS를 바꾸면 이 값도 함께 바꾼다 //
const POLL_TIMEOUT_MS = 435 * 1000;

// 토큰 소진 안내 — useAiChat.js 동일 문구 (dual sync) //
const TOKEN_EXHAUSTED_MESSAGE = '이번 달 AI 토큰을 모두 사용했어요. 다음 달에 다시 이용하시거나 토큰을 충전해 주세요.';

// 로딩 문구 — useAiChat LOADING_MESSAGES 동일 세트 (dual sync). LLM 자유생성 X(드리프트 차단) //
const LOADING_MESSAGES = {
  ko: [
    "잠깐만요!",
    "확인해볼게요!",
    "찾아보고 있어요!",
    "거의 다 됐어요!",
    "준비하고 있어요!",
    "금방 알려드릴게요!",
    "조금만 기다려주세요!",
    "열심히 찾고 있어요!",
  ],
  koInformal: [
    "잠깐만!",
    "확인해볼게!",
    "찾아보고 있어!",
    "거의 다 됐어!",
    "준비하고 있어!",
    "금방 알려줄게!",
    "조금만 기다려!",
    "열심히 찾고 있어!",
  ],
  en: [
    "One moment, please!",
    "Let me check that!",
    "Looking it up!",
    "Almost there!",
    "Getting it ready!",
    "I'll let you know shortly!",
    "Just a moment, please!",
    "Searching now!",
  ],
  ja: [
    "少々お待ちください！",
    "確認いたしますね！",
    "お調べしています！",
    "もうすぐです！",
    "準備しております！",
    "すぐにお伝えしますね！",
    "お探ししています！",
    "少しお待ちください！",
  ],
};

// renderJson(화면 재현 정본) → 메시지 채널 플래그 //
// 서버는 표/차트/SMS를 개별 필드가 아니라 renderJson 문자열 하나로 내려준다(displayChannels.buildRenderJson).
// renderJson 없이 저장된 턴은 finalText 평문이므로 파싱 실패는 채널 없음으로 떨어뜨린다 //
function parseRenderJson(renderJson) {
  if (!renderJson) return {};

  try {
    const parsed = JSON.parse(renderJson);

    if (parsed?.text === undefined) return {};

    // salesViewData는 weeksData(렌더 페이로드) 있을 때만 부착 — 코어 v1 요약판은 로그/검증용이라 미부착 (SalesViewRenderer:38 weeksData.map 무가드 크래시 차단) //
    return {
      ...(parsed.displayItems ? { displayItems: parsed.displayItems } : {}),
      ...(Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length > 0 ? { extraDisplays: parsed.extraDisplays } : {}),
      ...(parsed.vizData ? { vizData: parsed.vizData } : {}),
      ...(parsed.salesViewData?.weeksData ? { salesViewData: parsed.salesViewData } : {}),
      ...(parsed.revenueChartData ? { revenueChartData: parsed.revenueChartData } : {}),
      ...(parsed.smsRecommendation ? { smsRecommendation: restoreSmsFromSave(parsed.smsRecommendation) } : {}),
    };
  }
  catch {
    return {};
  }
}

export default function useAiChatServer() {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [tokenExhausted, setTokenExhausted] = useState(false);
  const [usagePercent, setUsagePercent] = useState(0);
  const [storeName, setStoreName] = useState('');

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(`session_${uuidv4()}`);
  const [messageRatings, setMessageRatings] = useState({});

  const chatMessagesContainerRef = useRef(null);
  const chatMessagesRef = useRef(chatMessages);
  chatMessagesRef.current = chatMessages;

  // 메시지별 대화 저장 메타 { [messageId]: { conversationId, userInput, intention, elapsedMs } } //
  const conversationMetaRef = useRef({});

  const isCancelledRef = useRef(false);
  const loadingIndexRef = useRef(0);
  const tokenExhaustedRef = useRef(false);

  // ============================================
  // 사용량 게이지 초기화 — 권한 조회 (키 복호화 없음: 키는 서버 소유) //
  // ============================================
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai_manage/permission', 'GET');

          if (responseData.status === 200 && responseData.data) {
            const data = responseData.data;

            setStoreName(data.storeName || '');
            if (typeof data.usagePercent === 'number') setUsagePercent(data.usagePercent);

            if (data.tokenExhausted === true) {
              setUsagePercent(100);
              tokenExhaustedRef.current = true;
              setTokenExhausted(true);
            }
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[AgentServer] permission error:', error);
        }
      }
    )();
  }, []);

  // ============================================
  // 채팅 스크롤 처리 (useAiChat 스페이서 로직 동일 — dual sync) //
  // ============================================
  const scrollTargetIdRef = useRef(null);
  const activeAiMsgIdRef = useRef(null);
  const initialSpacerHeightRef = useRef(0);

  function resetSpacer() {
    activeAiMsgIdRef.current = null;
    initialSpacerHeightRef.current = 0;
    const container = chatMessagesContainerRef.current;
    if (container) container.style.setProperty('--after-spacer-height', '0px');
  }

  useEffect(() => {
    // 1. 사용자 메시지 전송 직후 → 빈공간 생성 + 상단 스크롤
    const targetId = scrollTargetIdRef.current;
    if (targetId) {
      scrollTargetIdRef.current = null;
      const container = chatMessagesContainerRef.current;
      if (container) {
        const spacer = container.clientHeight;
        initialSpacerHeightRef.current = spacer;
        container.style.setProperty('--after-spacer-height', `${spacer}px`);
        requestAnimationFrame(() => {
          const target = container.querySelector(`[data-message-id="${targetId}"]`);
          if (target) {
            target.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        });
      }
      return;
    }

    // 2. AI 응답 타이핑 중 → 응답 높이만큼 빈공간 차감
    if (activeAiMsgIdRef.current && initialSpacerHeightRef.current > 0) {
      const container = chatMessagesContainerRef.current;
      if (container) {
        const aiMsgEl = container.querySelector(`[data-message-id="${activeAiMsgIdRef.current}"]`);
        const aiHeight = aiMsgEl ? aiMsgEl.offsetHeight : 0;
        const spacerHeight = Math.max(0, initialSpacerHeightRef.current - aiHeight);
        container.style.setProperty('--after-spacer-height', `${spacerHeight}px`);
      }
    }
  }, [chatMessages]);

  // 3. 응답 종료 시 → 빈공간 제거 //
  useEffect(() => {
    if (!isResponding) resetSpacer();
  }, [isResponding]);

  // 4. 화면 리사이즈 시 → 빈공간 제거 //
  useEffect(() => {
    function onResize() { resetSpacer(); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 5. 언마운트 cleanup — 진행 중 폴링 중단 //
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
    };
  }, []);

  // ============================================
  // 채팅 메시지 관리 (추가/업데이트 — useAiChat 동일) //
  // ============================================
  // 사용자 메시지 추가 //
  function addUserMessage(text) {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: false, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 추가 //
  function addAiMessage(text = '') {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: true, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 업데이트 (flags 값이 함수이면 이전 값을 인자로 전달하여 부분 업데이트) //
  function updateAiMessage(id, text, flags = {}) {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id !== id) return msg;

      const resolved = {};
      for (const [key, val] of Object.entries(flags)) {
        resolved[key] = typeof val === 'function' ? val(msg[key]) : val;
      }

      return {
        ...msg,
        ...(text !== undefined ? { text } : {}),
        ...resolved,
      };
    }));
  }

  // 로딩 메시지 표시 (shimmer) — 입력 언어·말투 감지 후 세트 순환 (서버 세션의 영속 톤 pref 미반영 — P1) //
  function showToolLoading(id, userInput) {
    const lang = detectResponseLanguage(userInput || '');
    const register = detectSpeechRegister(userInput || '');
    const key = (lang === 'ko' && register === 'informal') ? 'koInformal' : lang;
    const set = LOADING_MESSAGES[key] || LOADING_MESSAGES.ko;
    const msg = set[loadingIndexRef.current % set.length];
    loadingIndexRef.current++;
    updateAiMessage(id, msg, { isToolLoading: true });
  }

  // 로딩 해제 → 일반 메시지로 전환 //
  function clearToolLoading(id) {
    setChatMessages(prev => prev.map(msg => msg.id === id ? { ...msg, isToolLoading: false } : msg));
  }

  // 타이핑 효과 (최종 응답을 한 글자씩 — useAiChat 동일 3자/15ms) //
  async function typeMessage(id, text, chunkSize = 3, delayMs = 15) {
    clearToolLoading(id);
    let current = '';
    for (let i = 0; i < text.length; i += chunkSize) {
      if (isCancelledRef.current) break;
      current += text.slice(i, i + chunkSize);
      updateAiMessage(id, current);
      await new Promise(r => setTimeout(r, delayMs));
    }
    if (!isCancelledRef.current) {
      updateAiMessage(id, text);
    }
  }

  // ============================================
  // 평가 (useAiChat 동일 — POST ai-log/rating) //
  // ============================================
  const handleRateMessage = async (messageId, score, reason = null, comment = null) => {
    const meta = conversationMetaRef.current[messageId];
    if (!meta?.conversationId) return;

    const prevRating = messageRatings[messageId] || null;

    setMessageRatings(prev => ({ ...prev, [messageId]: { score, reason, comment } }));

    const fetchModule = new FetchModule();
    let isSaved = false;

    try {
      const res = await fetchModule.postDataVersion2('ai-log/rating', 'POST', {
        conversationId: meta.conversationId,
        sessionId,
        score,
        reason,
        comment,
      });
      isSaved = res?.status === 200;
    }
    catch (error) {
      isSaved = false;
    }

    // 저장 실패 시 조용히 이전 상태로 복귀 (채팅 동작 무영향) //
    if (!isSaved) setMessageRatings(prev => ({ ...prev, [messageId]: prevRating }));
  };

  // 평가 가능 여부 — 저장된 turn(conversationId 보유)만 평가 UI 노출 //
  const isMessageRatable = (messageId) => !!conversationMetaRef.current[messageId]?.conversationId;

  // ============================================
  // 턴 실행 — 접수 후 폴링 (Spring api3 경유) //
  // ============================================
  // 턴 접수 — turnId 발급. 409는 같은 세션에 진행 중인 턴이 있다는 뜻이고,
  // 그때도 data.turnId가 오므로 그 턴을 이어서 폴링한다(중단했던 턴을 잃지 않는다) //
  async function acceptAgentRun(userInput) {
    const fetchModule = new FetchModule();
    const responseData = await fetchModule.postDataVersion3('ai-agent/run/accept', 'POST', { sessionId, input: userInput });

    if (responseData.status === 200 || responseData.status === 409) return responseData.data?.turnId || null;

    return null;
  }

  // 턴 결과 조회 — running이면 간격을 두고 다시 조회한다.
  // 중단(isCancelledRef)·상한(435초)·조회 실패(404 포함)는 전부 null로 마무리한다 //
  async function pollAgentRun(turnId) {
    const fetchModule = new FetchModule();
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let attempt = 0;

    while (Date.now() < deadline) {
      const intervalSeconds = POLL_INTERVAL_SECONDS[attempt] ?? POLL_FIXED_INTERVAL_SECONDS;

      attempt++;

      await new Promise(r => setTimeout(r, intervalSeconds * 1000));

      if (isCancelledRef.current) return null;

      const responseData = await fetchModule.getDataVersion3(`ai-agent/run/${turnId}`, 'GET');

      if (responseData.status !== 200) return null;

      if (responseData.data?.state !== 'running') return responseData.data;
    }

    return null;
  }

  // 메시지 전송 //
  const handleSend = async () => {
    // 토큰 소진 — 서버 호출 없이 안내 메시지만 노출 //
    if (tokenExhaustedRef.current) {
      if (!chatInput.trim() || isResponding) return;

      setChatInput('');
      addAiMessage(TOKEN_EXHAUSTED_MESSAGE);
      return;
    }

    if (!chatInput.trim() || isResponding) return;

    const userInput = chatInput.trim();
    setChatInput('');
    setIsResponding(true);
    isCancelledRef.current = false;

    const userMsgId = addUserMessage(userInput);
    scrollTargetIdRef.current = userMsgId;
    const aiMessageId = addAiMessage('');
    activeAiMsgIdRef.current = aiMessageId;

    // [로딩 즉시 표시] 서버 응답을 기다리지 않고 제출 직후 로딩 문구 노출 //
    showToolLoading(aiMessageId, userInput);

    try {
      const turnId = await acceptAgentRun(userInput);

      if (!turnId) throw new Error('agent run accept failed');

      const data = await pollAgentRun(turnId);

      if (!data) throw new Error('agent run poll failed');

      // 실패 확정 — 서버 안내 문구를 그대로 노출 //
      if (data.state === 'failed') {
        updateAiMessage(aiMessageId, data.message || RESPONSE_FAILED_RETRY, { isToolLoading: false });

        return;
      }

      // 평가용 대화 메타 저장 — turnId가 곧 conversation_log.id(=conversationId) //
      conversationMetaRef.current[aiMessageId] = {
        conversationId: data.turnId || turnId,
        userInput,
        intention: data.intention || null,
        elapsedMs: data.elapsedMs || null,
      };

      // 사용량 게이지 갱신 + 소진 반영 //
      if (typeof data.usagePercent === 'number') setUsagePercent(data.usagePercent);

      if (data.tokenExhausted === true) {
        tokenExhaustedRef.current = true;
        setTokenExhausted(true);
        setUsagePercent(100);
      }

      const finalResponse = data.finalText || (isCancelledRef.current ? CANCELLED_RESPONSE : RESPONSE_FAILED_RETRY);
      await typeMessage(aiMessageId, finalResponse);

      // 타이핑 종료 후 표/차트/SMS 부착 — 서버는 renderJson 문자열 하나로 내려준다 //
      updateAiMessage(aiMessageId, finalResponse, {
        isToolLoading: false,
        isNewResponse: true,
        ...parseRenderJson(data.renderJson),
      });

      // 서버가 첫 턴에 세션 타이틀 저장 → 목록 갱신 //
      loadSessions();
    }
    catch (error) {
      if (TOOL_CALLING_LOG) console.error('[AgentServer] handleSend error:', error);
      const fallback = isCancelledRef.current ? CANCELLED_RESPONSE : RESPONSE_FAILED_RETRY;
      updateAiMessage(aiMessageId, fallback, { isToolLoading: false });
    }
    finally {
      setIsResponding(false);
    }
  };

  // 응답 중단 — 폴링만 멈춘다. 서버 턴은 계속 돌아 결과가 DB에 저장된다.
  // 같은 세션에 곧바로 다시 질문하면 접수가 409로 그 턴의 turnId를 돌려주므로 결과를 이어받는다 //
  const handleStop = () => {
    isCancelledRef.current = true;
    setIsResponding(false);
  };

  // ============================================
  // 세션 관련 함수 (useAiChat 동일 — Spring API 직접 호출) //
  // ============================================
  // 세션 목록 초기 로드 //
  useEffect(() => {
    loadSessions();
  }, []);

  // 세션 목록 불러오기 //
  const loadSessions = () => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai-chat/sessions', 'GET');

          if (responseData.status === 200) {
            setSessions(responseData.data || []);
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 대화 내역 불러오기 — 화면 복원은 renderJson, 버퍼 복원은 서버(session-state) 책임 //
  const loadConversations = (targetSessionId) => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(`ai-chat/sessions/${targetSessionId}`, 'GET');

          if (responseData.status === 200 && responseData.data) {
            const messages = [];
            const restoredRatings = {};

            responseData.data.forEach(entry => {
              messages.push({ id: `msg_${uuidv4()}`, text: entry.requestMessage, isAi: false });

              // AI 응답 복원 — JSON(텍스트 + 테이블 + SMS) 또는 텍스트 원문 //
              let aiMsg = { id: `msg_${uuidv4()}`, isAi: true };
              try {
                const parsed = JSON.parse(entry.responseMessage);
                if (parsed?.text !== undefined) {
                  aiMsg.text = parsed.text || '';
                  if (parsed.displayItems) aiMsg.displayItems = parsed.displayItems;
                  if (Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length > 0) aiMsg.extraDisplays = parsed.extraDisplays;
                  if (parsed.smsRecommendation) aiMsg.smsRecommendation = restoreSmsFromSave(parsed.smsRecommendation);
                  if (parsed.salesViewData) aiMsg.salesViewData = parsed.salesViewData;
                  if (parsed.revenueChartData) aiMsg.revenueChartData = parsed.revenueChartData;
                  if (parsed.vizData) aiMsg.vizData = parsed.vizData;
                }
                else {
                  aiMsg.text = entry.responseMessage;
                }
              }
              catch {
                aiMsg.text = entry.responseMessage;
              }
              messages.push(aiMsg);

              // 복원된 메시지에 대해 conversationMetaRef 설정 //
              if (entry.id) {
                conversationMetaRef.current[aiMsg.id] = {
                  conversationId: entry.id,
                  userInput: entry.requestMessage,
                  intention: entry.intention || null,
                  elapsedMs: entry.elapsedMs || null,
                };

                // 저장된 사용자평가 복원 //
                if (entry.score === 1 || entry.score === -1) {
                  restoredRatings[aiMsg.id] = {
                    score: entry.score,
                    reason: entry.reason ?? null,
                    comment: entry.comment ?? null,
                  };
                }
              }
            });

            setSessionId(targetSessionId);
            setChatMessages(messages);
            setMessageRatings(restoredRatings);
            resetSpacer();
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 삭제 //
  const deleteSession = (targetSessionId) => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(`ai-chat/sessions/${targetSessionId}`, 'DELETE');

          if (responseData.status === 200) {
            if (sessionId === targetSessionId) handleNewChat();

            loadSessions();
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[Session] delete error:', error);
        }
      }
    )();
  };

  // 새 채팅 세션 생성 — 서버 세션은 TTL로 자연 소멸 //
  const handleNewChat = () => {
    const newSessionId = `session_${uuidv4()}`;
    setSessionId(newSessionId);
    setChatMessages([]);
    setMessageRatings({});
    resetSpacer();
  };

  // ============================================
  // SMS 핸들러 — 발송은 기존 Spring API 재사용 //
  // ============================================
  // 재작성(generate)은 LLM 필요 → clientRef null 가드로 자연 강등 (P1: 서버 위임 예정) //
  const { handleSmsSend, handleIndividualSmsSend, handleIndividualSmsGenerate } = useMemo(
    () =>
      createSmsHandlers({
        clientRef: { current: null },
        storeName,
        sessionId,
        chatMessagesRef,
        conversationMetaRef,
        updateAiMessage,
        loggedCompletion: null,
        resendTurnWithSms: null,
      }),
    [storeName, sessionId]
  );

  return {
    // 상태
    chatMessages,
    chatInput,
    isResponding,
    tokenExhausted,
    usagePercent,
    sessions,
    sessionId,
    chatMessagesContainerRef,
    messageRatings,

    // 액션
    setChatInput,
    handleSend,
    handleStop,
    handleNewChat,
    loadConversations,
    deleteSession,
    handleRateMessage,
    isMessageRatable,

    // SMS 핸들러 //
    handleSmsSend,
    handleIndividualSmsSend,
    handleIndividualSmsGenerate,
  };
}
